import React, { useEffect, useRef, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Upload, X, FileImage, Eye, Loader2 } from 'lucide-react';
import { cn } from '@/common/utils';
import { PasseriniAPI } from '@/services/apiService';

interface ImageUploadFieldProps {
  label: string;
  value: File | null;
  onChange: (file: File | null) => void;
  existingFile?: {
    fileName: string;
    fileSize?: number;
    formattedSize?: string;
    fileUrl?: string;
    mimeType?: string;
  } | null;
  onRemoveExisting?: () => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  accept?: string;
  maxSize?: number; // en MB
}

export const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
  label,
  value,
  onChange,
  existingFile = null,
  onRemoveExisting,
  error,
  disabled = false,
  required = true,
  accept = 'image/jpeg,image/png,application/pdf',
  maxSize = 50, // 50MB por default
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [existingPreviewUrl, setExistingPreviewUrl] = useState<string | null>(null);
  const [isLoadingExistingPreview, setIsLoadingExistingPreview] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let objectUrlToRevoke: string | null = null;

    const loadExistingPreview = async () => {
      if (
        value ||
        !existingFile?.fileUrl ||
        !existingFile?.mimeType?.startsWith('image/')
      ) {
        setExistingPreviewUrl(null);
        setIsLoadingExistingPreview(false);
        return;
      }

      setIsLoadingExistingPreview(true);
      try {
        const response = await PasseriniAPI.get(existingFile.fileUrl.replace(/^\/api/, ''), {
          responseType: 'blob',
        });

        if (!isMounted) return;
        objectUrlToRevoke = URL.createObjectURL(response.data);
        setExistingPreviewUrl(objectUrlToRevoke);
      } catch {
        if (isMounted) {
          setExistingPreviewUrl(null);
        }
      } finally {
        if (isMounted) {
          setIsLoadingExistingPreview(false);
        }
      }
    };

    loadExistingPreview();

    return () => {
      isMounted = false;
      if (objectUrlToRevoke) {
        URL.revokeObjectURL(objectUrlToRevoke);
      }
    };
  }, [existingFile?.fileUrl, existingFile?.mimeType, value]);

  const validateFile = (file: File): string | null => {
    // Validar tamaño
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > maxSize) {
      return `El archivo excede el tamaño máximo de ${maxSize}MB`;
    }

    // Validar tipo
    const acceptedTypes = accept.split(',').map(t => t.trim());
    if (!acceptedTypes.some(type => file.type.match(new RegExp(type.replace('*', '.*'))))) {
      return 'Tipo de archivo no permitido';
    }

    return null;
  };

  const handleFile = (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      alert(validationError);
      return;
    }

    onChange(file);

    // Generar preview si es imagen
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;

    const files = e.target.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  };

  const handleRemove = () => {
    if (value) {
      onChange(null);
      setPreview(null);
    } else if (existingFile && onRemoveExisting) {
      onRemoveExisting();
    }

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleClick = () => {
    if (!disabled) {
      inputRef.current?.click();
    }
  };

  const handleOpenPreview = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (disabled || !existingFile?.fileUrl) {
      return;
    }

    try {
      const response = await PasseriniAPI.get(existingFile.fileUrl.replace(/^\/api/, ''), {
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(response.data);
      const newWindow = window.open(blobUrl, '_blank');
      if (!newWindow) {
        URL.revokeObjectURL(blobUrl);
      } else {
        newWindow.addEventListener('load', () => {
          setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        });
      }
    } catch {
      alert('No se pudo previsualizar el archivo.');
    }
  };

  const hasCurrentFile = Boolean(value || existingFile);
  const currentFileName = value?.name ?? existingFile?.fileName ?? '';
  const currentSize = value
    ? `${(value.size / 1024).toFixed(2)} KB`
    : existingFile?.formattedSize ?? (typeof existingFile?.fileSize === 'number'
      ? `${(existingFile.fileSize / 1024).toFixed(2)} KB`
      : '');
  const imagePreviewUrl = preview || existingPreviewUrl;
  const canPreviewExistingFile = Boolean(existingFile?.fileUrl && !value);

  return (
    <div className="space-y-2 min-w-0">
      <Label>
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>

      <div
        className={cn(
          'relative rounded-lg border-2 border-dashed p-4 transition-colors overflow-hidden',
          dragActive && !disabled && 'border-primary bg-primary/5',
          !dragActive && !error && 'border-gray-300 bg-gray-50',
          error && 'border-red-300 bg-red-50',
          disabled && 'opacity-50 cursor-not-allowed',
          !disabled && 'cursor-pointer hover:border-gray-400'
        )}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleChange}
          disabled={disabled}
          className="hidden"
        />

        {!hasCurrentFile ? (
          <div className="flex flex-col items-center justify-center py-4 text-center">
            <Upload className="h-10 w-10 text-gray-400 mb-2" />
            <p className="text-sm text-gray-600">
              <span className="font-medium text-primary">Click para subir</span> o arrastre el archivo
            </p>
            <p className="text-xs text-gray-500 mt-1">
              JPG, PNG o PDF (máx. {maxSize}MB)
            </p>
          </div>
        ) : (
          <div className="flex items-center gap-3 min-w-0">
            {isLoadingExistingPreview ? (
              <div className="h-20 w-20 flex-shrink-0 flex items-center justify-center bg-gray-100 rounded border border-gray-200">
                <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
              </div>
            ) : imagePreviewUrl ? (
              <img
                src={imagePreviewUrl}
                alt="Preview"
                className="h-20 w-20 flex-shrink-0 object-cover rounded border border-gray-200"
              />
            ) : (
              <div className="h-20 w-20 flex-shrink-0 flex items-center justify-center bg-gray-100 rounded border border-gray-200">
                <FileImage className="h-8 w-8 text-gray-400" />
              </div>
            )}

            <div className="flex-1 min-w-0 overflow-hidden">
              <p className="text-sm font-medium text-gray-900 truncate" title={currentFileName}>
                {currentFileName}
              </p>
              <p className="text-xs text-gray-500">
                {currentSize}
              </p>
            </div>

            {canPreviewExistingFile && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="flex-shrink-0"
                onClick={handleOpenPreview}
                disabled={disabled}
                title="Previsualizar archivo"
              >
                <Eye className="h-4 w-4" />
              </Button>
            )}

            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
              disabled={disabled}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};
